import { useState } from "react";
import { currency as formatCurrency } from "../../../lib/format";
import { Section } from "./section";

interface SummaryData {
  reinstatementEstimate: number | null;
  reinstatementEstimateRounded: number | null;
  reinstatementInflation: number | null;
  reinstatementInflationRounded: number | null;
  totalDemolition: number | null;
  totalDemolitionRounded: number | null;
  drcForFsl: number | null;
  drcForFslRounded: number | null;
  reinstatementPerUnit: number | null;
  numberOfUnits: number | null;
}

function currency(v: number | null): string {
  if (v == null || v === 0) return "N/A";
  return formatCurrency(v);
}

function SummaryRow({
  label,
  calculated,
  rounded,
  showDetail,
}: {
  label: string;
  calculated: number | null;
  rounded: number | null;
  showDetail: boolean;
}): React.ReactElement {
  const hasDiff = calculated != null && rounded != null && calculated !== rounded;
  const diff = hasDiff ? rounded - calculated : null;

  return (
    <div className="flex items-baseline justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="text-right">
        <span className="text-sm font-semibold tabular-nums">{currency(rounded ?? calculated)}</span>
        {showDetail && hasDiff && (
          <span className="text-xs text-muted-foreground ml-2">
            (calc: {currency(calculated)}, adj: {diff && diff > 0 ? "+" : ""}
            {currency(diff)})
          </span>
        )}
      </div>
    </div>
  );
}

export function SectionSummary({ data }: { data: SummaryData }): React.ReactElement {
  const [showDetail, setShowDetail] = useState(false);

  // Calculate per-unit if missing but reinstatement and units exist
  const perUnit =
    data.reinstatementPerUnit ??
    (data.reinstatementEstimate && data.numberOfUnits && data.numberOfUnits > 0
      ? Math.round(data.reinstatementEstimate / data.numberOfUnits)
      : null);

  return (
    <Section id="summary" title="Summary">
      <div className="max-w-lg divide-y">
        <SummaryRow
          label="Reinstatement Cost Estimate"
          calculated={data.reinstatementEstimate}
          rounded={data.reinstatementEstimateRounded}
          showDetail={showDetail}
        />
        <SummaryRow
          label="Reinstatement Inflation"
          calculated={data.reinstatementInflation}
          rounded={data.reinstatementInflationRounded}
          showDetail={showDetail}
        />
        <SummaryRow
          label="Demolition"
          calculated={data.totalDemolition}
          rounded={data.totalDemolitionRounded}
          showDetail={showDetail}
        />
        <SummaryRow
          label="DRC for Fire and Emergency Levy"
          calculated={data.drcForFsl}
          rounded={data.drcForFslRounded}
          showDetail={showDetail}
        />
        {perUnit != null && (
          <div className="flex items-baseline justify-between py-1.5">
            <span className="text-sm text-muted-foreground">
              Per Unit ({data.numberOfUnits ?? 1} unit{(data.numberOfUnits ?? 1) === 1 ? "" : "s"})
            </span>
            <span className="text-sm font-semibold tabular-nums">{currency(perUnit)}</span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => setShowDetail(!showDetail)}
        className="text-xs text-primary hover:underline mt-2"
      >
        {showDetail ? "Hide calculation detail" : "Show calculation detail"}
      </button>
    </Section>
  );
}
